import React from "react";
import "./smile-pages.css";

export default function WhyAreSmilesMorePowerfulWhenLookingAtFamily() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">
      <h1>Why Are Smiles More Powerful When Looking at Family?</h1>

      <p>
        Smiles are more powerful when looking at family—in person, through
        videos, or pictures—because they tap into deep emotional, biological,
        and psychological bonds that intensify positive facial expressions.
        When you smile at a loved one—or receive a smile from them—that smile
        carries far more meaning than one from a stranger.
      </p>

      <hr />

      {/* 1. Stronger Brain Activation */}
      <h3>🧠 1. Stronger Brain Activation in the Reward System</h3>
      <ul>
        <li>
          Seeing a family member smile triggers stronger activity in your
          brain’s reward centers, especially the ventral striatum and amygdala.
        </li>
        <li style={{ marginTop: "8px" }}>
          Your brain assigns greater emotional and social value to people with
          whom you have close relationships.
        </li>
      </ul>
      <p className="source-text">
        📚 <strong>Study:</strong> Leibenluft et al. (2004) – Viewing one’s own
        child activated reward circuitry more strongly than seeing other
        children.
      </p>

      <hr />

      {/* 2. Oxytocin */}
      <h3>❤️ 2. Oxytocin: The “Bonding Hormone” Increases</h3>
      <ul>
        <li>Family smiles can increase the release of oxytocin.</li>
        <li style={{ marginTop: "8px" }}>
          Oxytocin is linked to bonding, trust, and emotional warmth.
        </li>
      </ul>
      <p className="source-text">
        📚 <strong>Source:</strong> Zak (2011) – Oxytocin plays a major role in
        social bonding.
      </p>

      <hr />

      {/* 3. Shared history */}
      <h3>👨‍👩‍👧‍👦 3. Shared History Enhances Emotional Meaning</h3>
      <ul>
        <li>A family smile carries layers of shared memories and familiarity.</li>
        <li style={{ marginTop: "8px" }}>
          Past emotional experiences—joyful or difficult—make family smiles
          more meaningful and comforting.
        </li>
      </ul>

      <hr />

      {/* 4. Evolutionary psychology */}
      <h3>🧬 4. Evolutionary Psychology: Family = Survival</h3>
      <ul>
        <li>From an evolutionary viewpoint, bonding with kin increased survival.</li>
        <li style={{ marginTop: "8px" }}>
          Humans evolved to respond more strongly to emotional signals—like
          smiles—from relatives.
        </li>
      </ul>
      <p className="source-text">
        📚 <strong>Source:</strong> Dunbar (1998) – Social bonding shaped human
        evolution.
      </p>

      <hr />

      {/* 5. Mirror Neurons */}
      <h3>👁️‍🗨️ 5. Mirror Neurons Respond More Strongly to Familiar Faces</h3>
      <ul>
        <li>
          The mirror neuron system reacts more intensely when we see a familiar
          or loved one’s face.
        </li>
        <li style={{ marginTop: "8px" }}>
          A loved one’s smile triggers a stronger reflexive smile in return,
          deepening emotional synchronization.
        </li>
      </ul>
      <p className="source-text">
        📚 <strong>Study:</strong> Carr et al. (2003) – Mirror neurons are more
        engaged by emotionally familiar faces.
      </p>

      <hr />

      {/* Summary */}
      <h3>✅ Summary: Why Family Smiles Hit Harder</h3>
      <table className="summary-table">
        <thead>
          <tr>
            <th>Reason</th>
            <th>Effect</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Stronger reward activation</td>
            <td>Boosts happiness and emotional resonance</td>
          </tr>
          <tr>
            <td>Oxytocin release</td>
            <td>Increases bonding and trust</td>
          </tr>
          <tr>
            <td>Shared memories</td>
            <td>Deepens emotional meaning</td>
          </tr>
          <tr>
            <td>Evolutionary bonding</td>
            <td>Signals safety and connection</td>
          </tr>
          <tr>
            <td>Mirror neuron activation</td>
            <td>Encourages reciprocal smiling</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* Keys to remember */}
      <h3>🔑 Keys to Remember</h3>
      <p>
        Smiles from family members are more powerful because they activate
        stronger emotional, neurological, and hormonal responses tied to trust,
        love, and lifelong connection. These smiles don’t just feel good—they
        feel like home.
      </p>
      <p style={{ marginTop: "12px" }}>
        <strong>That Smile App</strong> is here to help strengthen and nurture
        those meaningful family connections.
      </p>
    </div>
  );
}
